import { useMemo } from "react";
import { EntityTypes } from "@vzbl/integrations";
import { useApplicationContext } from "src/hooks/useApplication";
import { Time } from "src/components/Time";
import { useEntityContext } from "../useEntityContext";
import { VzblTag } from "../components/VzblTag";
import { TabbedWork } from "../components/TabbedWork";
import { Insights } from "../Insights";
import { TimeMachine, TimeMachineComponent } from "../TimeMachine";
import { useWorkTabs } from "../components/worktabs";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { MetricTag, SummaryTag } from "./components/Tags";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { toHeaderAttributes } from "./components/HeaderAttributes";
import { EntityTabs } from "./components/EntityTabs";
import { ProgressMetric } from "./components/ProgressMetric";
import { SentencesList } from "./components/SentencesList";

export const PullRequestReviewEntity = () => {
  const context = useEntityContext();
  const current = context.dataRef.current.entity;
  const label = current.properties.sourceUrl?.split("/").at(-1);
  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          entity={current}
          label={label}
          attributes={usePullRequestReviewHeaderAttributes(current)}
        />
      }
      summaryPanel={
        <SummaryPanel
          title={current.properties.pullRequestTitle}
          description={current.properties.body}
        />
      }
      insightsPanel={
        <InsightsPanel cards={usePullRequestReviewInsights(current)} />
      }
    >
      <EntityTabs
        propertiesSkipList={[
          "repository",
          "state",
          "submittedAt",
          "vzblSummary",
          "vzblRiskScore",
          "vzblRiskExplanation",
          "vzblClassification",
        ]}
        entity={current}
        tabs={usePullRequestReviewTabs(current)}
      />
    </EntityScreen>
  );
};

export function usePullRequestReviewHeaderAttributes(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];
  const { repository, state, submittedAt } = entity.properties;
  return useMemo(
    () =>
      toHeaderAttributes({
        orgId,
        context,
        entityType,
        attributes: [
          { name: "repository", text: repository },
          { name: "state", text: state },
          {
            name: "submitted at",
            text: <Time value={submittedAt} />,
          },
        ],
      }),
    [context, orgId, entityType, repository, state, submittedAt]
  );
}

export function usePullRequestReviewTabs(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  return [
    {
      key: "overview",
      id: "overview",
      label: "Overview",
      children: (
        <div style={{ minHeight: "500px", width: "100%" }}>
          <Insights entity={context.dataRef.current.entity} />
        </div>
      ),
    },
    {
      key: "2",
      id: "2",
      label: "Work",
      children: (
        <TabbedWork
          title="Work"
          items={useWorkTabs(orgId, entity.properties.uuid, [
            { entityTypes: EntityTypes.Issue },
            { entityTypes: EntityTypes.Sprint },
            { entityTypes: EntityTypes.Commit },
            {
              entityTypes: EntityTypes.PullRequestReviewComment,
              label: "Comments",
            },
            { entityTypes: EntityTypes.Branch, label: "Branches" },
            { entityTypes: EntityTypes.Release },
            { entityTypes: EntityTypes.Repository, label: "Repositories" },
            { entityTypes: EntityTypes.CicdPipeline, label: "Deployments" },
          ])}
        />
      ),
    },

    {
      key: "5",
      id: "5",
      label: "History",
      children: (
        <TimeMachine>
          <TimeMachineComponent />
        </TimeMachine>
      ),
    },
  ];
}

export function usePullRequestReviewInsights(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];

  return [
    entity.properties.vzblSummary ? (
      <ActionCard
        badge={<SummaryTag />}
        label="Summary"
        value={<SentencesList showNa data={entity.properties.vzblSummary} />}
      />
    ) : undefined,
    entity.properties.vzblRiskScore ? (
      <ActionCard
        key="risk-score"
        badge={<MetricTag />}
        label="Risk Score"
        value={
          <ProgressMetric
            lowerBetter
            value={entity.properties.vzblRiskScore}
            data={entity.properties.vzblRiskExplanation}
          />
        }
      />
    ) : undefined,
    entity.properties.vzblClassification ? (
      <ActionCard
        key="classification"
        badge={<MetricTag />}
        label="Classification"
        value={
          <VzblTag
            orgId={orgId}
            context={context}
            text={entity.properties.vzblClassification}
            color="none"
            linkParams={{
              orgId,
              entityType: entityType,
              attribute: "vzblClassification",
              value: entity.properties.vzblClassification,
            }}
          />
        }
      />
    ) : undefined,
  ].filter(Boolean);
}
